import React, { useState } from "react";
import useForm from "../Common/FormComponent/useForm";
import FormComponent from "../Common/FormComponent/FormComponent";
import { Box, Button, TextField, Typography } from "@mui/material";
import ImageUploader from "../Common/ImageUploader";
import Label from "../Common/Label/Label";
import VideoUploader from "../Common/VideoUploader";
import {
  assessmentDetails,
  generalAssessmentDetails,
  generalVideoDetails,
  videoConfig,
  videoDurationConfig,
} from "./constants";
import CommunicationHubApis from "../../services/api/CommunicationHub";
import GoBack from "./GoBack";
import AddLanguageDialog from "./AddLanguageDialog";
import { useDispatch } from "react-redux";
import { setStatus } from "../../store/actions/User";
import {
  convertToTime,
  getVideoDuration,
  getVideoDurationByURL,
  videoUrlRegex,
} from "../../utils/video";
import useMutation from "../../hooks/useMutation";
import { useHistory } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { QuizContainer } from "../Courses/CourseForm/CourseFormStyles";
import QuizComponent from "../Courses/CourseForm/QuizComponent";
import styled from "styled-components";
import RenderIf from "../Common/RenderIf/RenderIf";
import SeriesSelector from "./SeriesSelector";
import { Add } from "@material-ui/icons";
import UploadImageWrapper from "./ImageUploadWrapper";
import UploadVideoWrapper from "./VideoUploadWrapper";

const REQUIRED_MESSAGE = "This field is required";

const validateAssessmentForm = (values) => {
  const errors = {};

  if (!values?.pass_percentage || values?.pass_percentage === "0")
    errors.pass_percentage = REQUIRED_MESSAGE;

  if (!values?.thumbnail?.portrait)
    errors["thumbnail.portrait"] = REQUIRED_MESSAGE;
  if (!values?.thumbnail?.landscape)
    errors["thumbnail.landscape"] = REQUIRED_MESSAGE;
  const noOfLanguages = values?.content?.length;
  if (noOfLanguages === 0)
    errors.content = "Please add at least one language set";
  else {
    if (
      new Set(values.content.map((language) => language.questions.length))
        .size !== 1
    ) {
      errors.content = "Please add the same no of questions in all languages";
    } else {
      // if the no of questions are same, then will check for options
      const noOfQuestions = values.content[0]?.questions?.length;
      const questions = values.content[0]?.questions;
      const content = values.content;
      if (noOfQuestions === 0) {
        errors.content = "Please add at least one question";
      } else if (questions[0].choices?.length < 2) {
        errors.content = "Please add at least two option";
      } else if (
        values.assessment_type === "QUIZ" &&
        questions[0].answers?.length === 0
      ) {
        errors.content = "Please add at least one answer";
      } else {
        const answers = new Set();
        const options = new Set();
        questions.forEach((_, index) => {
          content.forEach((lang) => {
            answers.add(lang.questions[index].answers.length);
            options.add(lang.questions[index].choices.length);
          });
        });
        if (values.assessment_type === "QUIZ" && answers.size !== 1) {
          errors.content = "Please mark the same answers in all languages";
        }
        if (options.size !== 1) {
          errors.content = "Please add the same options for all languages";
        }

        // checking contents of the questions for validation
        content.forEach((lang, contentIndex) => {
          lang.questions.forEach((question, questionIndex) => {
            const key = `content.${contentIndex}.questions.${questionIndex}`;
            if (question.question.length === 0) {
              errors[`${key}.question`] = "This field is required";
            }
            if (question.choices.filter((item) => item).length === 0) {
              errors[`${key}.choices`] = "This field is required";
            }
          });
        });
      }
    }
  }

  console.log({ errors });
  return errors;
};

const validateVideoForm = (values) => {
  const errors = {};
  if (!values?.tags || values?.tags?.length === 0)
    errors.tags = REQUIRED_MESSAGE;
  if (!values?.pass_percentage || values?.pass_percentage === "0")
    errors.pass_percentage = REQUIRED_MESSAGE;
  if (!values?.priority) errors.priority = REQUIRED_MESSAGE;
  if (!values?.thumbnail?.portrait)
    errors["thumbnail.portrait"] = REQUIRED_MESSAGE;
  if (!values?.thumbnail?.landscape)
    errors["thumbnail.landscape"] = REQUIRED_MESSAGE;
  if (values?.search_keywords && values.search_keywords?.length > 100)
    errors.search_keywords = "You can add max 100 keywords";
  if (values?.content?.length === 0)
    errors.content = "Please add a default language";
  (values?.content ?? []).forEach((item, index) => {
    const key = `content.${index}.`;
    if (!item.title) errors[key + "title"] = REQUIRED_MESSAGE;
    if (item?.title?.length > 50)
      errors[key + "title"] = "Length of title can be max 50";
    if (!item.description) errors[key + "description"] = REQUIRED_MESSAGE;
    if (item?.description?.length > 250)
      errors[key + "description"] = "Length of description can be max 250";
    if (!item.duration) errors[key + "duration"] = REQUIRED_MESSAGE;
  });
  return errors;
};

// to parse local state to API format
const parseAssessment = ({ questions }) => {
  return questions.reduce(
    (a, b) => {
      a.questions.push(b.question);
      a.options.push({ option: b.choices });
      a.correct_options.push({ indices: b.answers });
      a.question_types.push(b.type);
      return a;
    },
    {
      questions: [],
      options: [],
      correct_options: [],
      question_types: [],
    }
  );
};
// to API format to local state
const unParseAssessment = ({ initialData }) => {
  const questions = [];
  initialData.questions.forEach((item, index) => {
    const question = {
      question: item,
      type: initialData?.question_types?.[index],
      choices: initialData?.options?.[index]?.option,
      answers: initialData?.correct_options?.[index]?.indices,
    };
    questions.push(question);
  });
  delete initialData?.question_types;
  delete initialData?.options;
  delete initialData?.correct_options;
  return { ...initialData, questions };
};

const forms = {
  ASSESSMENT: generalAssessmentDetails,
  COMMUNICATION_TYPE_VIDEO: generalVideoDetails,
};

const validations = {
  ASSESSMENT: validateAssessmentForm,
  COMMUNICATION_TYPE_VIDEO: validateVideoForm,
};

const getInitialState = (elementType, initialData) => {
  switch (elementType) {
    case "COMMUNICATION_TYPE_VIDEO":
      return { ...initialData };
    case "ASSESSMENT":
      initialData.content = initialData.content.map((item) =>
        unParseAssessment({ item })
      );
      return {
        ...initialData,
        assessment_type: initialData.assessment_type ?? "QUIZ",
        pass_percentage: initialData.pass_percentage ?? 75,
        randomize_questions: {
          randomize_questions: !!initialData.randomize_questions,
        },
      };
    case "SERIES":
      return {
        ...initialData,
        sequence_details: [{}],
      };
  }
};
const ElementForm = ({ options, initialData, edit, element_type }) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const form = useForm({
    ...getInitialState(element_type, initialData),
    element_type,
    request_type: edit ? "UPDATE" : "CREATE",
    content: [
      {
        lang_code: "en",
        title: "",
        description: "",
        questions: [
          {
            question: "",
            type: "multi",
            choices: ["", "", "", ""],
            answers: [1, 2],
          },
        ],
      },
    ],
  });

  const [languages, setLanguages] = useState(new Set());

  const { mutate: createMutation } = useMutation(
    CommunicationHubApis.createElement
  );
  const { mutate: updateMutation } = useMutation(
    CommunicationHubApis.updateElement
  );

  const dispatch = useDispatch();

  const handleCreate = async () => {
    try {
      // validate here,
      const errors = validations[element_type](form.values);
      if (Object.values(errors).some((item) => !!item)) {
        dispatch(
          setStatus({
            message: "Please correct all the error(s)",
            type: "error",
          })
        );
        form.setAllErrors(errors);
      } else {
        const payload = {
          ...form.values,
          pass_percentage: parseInt(form.values.pass_percentage),
          buffer_hours: parseInt(form.values?.buffer_hours),
          tags: (form?.values?.tags ?? []).map((tag) => tag.value),
          ...(element_type === "ASSESSMENT" && {
            content: form.values.content.map((item) => ({
              ...item,
              ...parseAssessment({ questions: item.questions }),
            })),
            randomize_questions:
              !!form.values.randomize_questions?.randomize_questions,
          }),
        };
        if (edit) {
          await updateMutation(payload);
          history.push(pathname);
        } else {
          await createMutation(payload);
          history.push("/communication-hub");
        }
        dispatch(
          setStatus({
            message: "Successfully updated",
            type: "success",
          })
        );
      }
    } catch (err) {
      console.log({ err });
      dispatch(
        setStatus({
          message: "Something went wrong",
          type: "error",
        })
      );
    }
  };

  const handleDelete = (index) => {
    form.setFields((prev) => ({
      ...prev,
      content: prev.content.filter((item, i) => i !== index),
    }));
  };

  const handleAdd = (langCode) => {
    form.setFields((prev) => ({
      ...prev,
      content: [
        ...prev.content,
        {
          lang_code: langCode,
          title: "",
          description: "",
          ...(element_type === "COMMUNICATION_TYPE_VIDEO"
            ? { url: "", duration: "" }
            : element_type === "ASSESSMENT"
            ? {
                questions: [
                  {
                    question: "",
                    type: "single",
                    choices: [],
                    answers: [],
                  },
                ],
              }
            : {}),
        },
      ],
    }));
  };

  const makeDefaultLanguage = (index) => {
    form.setFields((prev) => ({
      ...prev,
      content: [
        prev.content[index],
        ...prev.content.filter((item, i) => i !== index),
      ],
    }));
  };

  const handleImage =
    (key) =>
    ({ file, url }) => {
      form.setFields((prev) => ({
        thumbnail: {
          ...prev.thumbnail,
          [key]: url,
        },
      }));
    };

  const handleVideoImage =
    (index) =>
    async ({ file, url }) => {
      const duration = await getVideoDuration(file);
      form.setFields((prev) => {
        prev.content[index].url = url;
        prev.content[index].duration = convertToTime(duration);
        return { content: [...prev.content] };
      });
    };

  const handleVideoUrl = (index, key) => async (e) => {
    const value = e.target.value;

    if (videoUrlRegex.test(value)) {
      try {
        form.handleValue(`content.${index}.url`, {
          regex: /\.(mp4|m3u8)$/,
          errorMessage: "The URL not seems to be a valid video URL",
        })(value);
        const duration = await getVideoDurationByURL(value);
        form.setFields((prev) => {
          prev.content[index].duration = convertToTime(duration);
          return { content: [...prev.content] };
        });
      } catch (err) {
        dispatch(
          setStatus({
            message: "Enter a valid url video",
            type: "error",
          })
        );
      }
    } else {
      form.setFields((prev) => {
        prev.content[index].url = value;
        return { content: [...prev.content] };
      });
      dispatch(
        setStatus({
          message: "Enter a valid url",
          type: "error",
        })
      );
    }
  };

  const generalDetails = forms[element_type];

  const handleQuestionAdd = (contentIndex) => {
    const content = form.values.content;
    content[contentIndex].questions = [
      ...(content[contentIndex].questions ?? []),
      {
        question: "",
        type: "single",
        choices: [],
        answers: [],
      },
    ];
    form.handleValue("content")([...content]);
  };
  const handleQuestionDelete = (contentIndex, questionIndex) => {
    const content = form.values.content;
    content[contentIndex].questions = content[contentIndex].questions.filter(
      (_, index) => index !== questionIndex
    );
    form.handleValue("content")([...content]);
  };

  const handleSelectionChange = (
    contentIndex,
    key,
    value,
    type,
    questionIndex,
    optionIndex
  ) => {
    console.log({ contentIndex, key, value, type, questionIndex, optionIndex });
    const content = form.values.content;
    // check of keys that needs array values
    if (optionIndex !== undefined) {
      if (key === "answers") {
        const answers = content[contentIndex].questions[questionIndex][key];
        const answer = optionIndex + 1;
        content[contentIndex].questions[questionIndex][key] = answers.includes(
          answer
        )
          ? answers.filter((item) => item !== answer)
          : [...answers, answer];
      } else {
        content[contentIndex].questions[questionIndex][key][optionIndex] =
          value;
      }
    } else {
      content[contentIndex].questions[questionIndex][key] = value;
    }
    console.log({ content });
    form.handleValue("content")([...content]);
  };

  const handleQuizOptionAddition = (contentIndex, questionIndex) => {
    const content = form.values.content;
    content[contentIndex].questions[questionIndex].choices.push("");
    form.handleValue("content")([...content]);
  };

  const handleQuizOptionDelete = (contentIndex, questionIndex, choiceIndex) => {
    const content = form.values.content;
    content[contentIndex].questions[questionIndex].choices = content[
      contentIndex
    ].questions[questionIndex].choices.filter(
      (_, index) => index !== choiceIndex
    );
    form.handleValue("content")([...content]);
  };

  const handleAddSection = () => {
    form.setFields((prev) => ({
      sequence_details: [...prev.sequence_details, {}],
    }));
  };

  const onSeriesSectionChange =
    (index) =>
    ({ values, errors, detailsApi }) => {
      console.log({ values, errors, detailsApi });
      if (detailsApi.data) {
        const langs = new Set(languages);

        detailsApi.data.content.forEach((item) => langs.add(item.lang_code));

        setLanguages(langs);
      }
      const sequenceDetails = form.values.sequence_details;
      sequenceDetails[index] = {
        ...values,
        languages: detailsApi?.data?.content?.length ?? 0,
      };

      form.setFields((prev) => ({
        sequence_details: [...sequenceDetails],
      }));
    };

  return (
    <Box flexDirection={"column"} display={"flex"} gap={4}>
      <Box display={"flex"} justifyContent={"space-between"}>
        <GoBack title={"Go Back"} />
        <Button color="error" variant="contained" onClick={handleCreate}>
          {edit ? "Update" : "Create"}
        </Button>
      </Box>
      <RenderIf condition={element_type !== "SERIES"}>
        <>
          <Box display="flex" gap={4} alignItems={"end"}>
            <Box
              maxWidth={"750px"}
              flex={1}
              display={"flex"}
              flexDirection={"column"}
              gap={2}
            >
              {generalDetails &&
                generalDetails.map((item) => (
                  <Box>
                    <FormComponent
                      config={item}
                      form={form}
                      key={item.id}
                      otherOptions={options[item?.props?.optionsKey ?? item.id]}
                    />
                    <ErrorLabel label={form.errors[item.id]} />
                  </Box>
                ))}
            </Box>

            <Box maxWidth={"750px"} flex={1} display={"flex"} gap={2}>
              <Box display={"flex"} flexDirection={"column"} gap={2}>
                <Label>Portrait thumbnail</Label>
                <UploadImageWrapper
                  UploadComponent={ImageUploader}
                  onUpload={handleImage("portrait")}
                  url={form.getValue("thumbnail.portrait")}
                />
                <ErrorLabel label={form.errors["thumbnail.portrait"]} />
              </Box>
              <Box display={"flex"} flexDirection={"column"} gap={2}>
                <Label>Landscape thumbnail</Label>
                <UploadImageWrapper
                  UploadComponent={ImageUploader}
                  onUpload={handleImage("landscape")}
                  url={form.getValue("thumbnail.landscape")}
                />
                <ErrorLabel label={form.errors["thumbnail.landscape"]} />
              </Box>
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            The thumbnail aspect ratio is 0.8 for portrait and 1.66 for landscape.
          </Typography>
          <Box borderBottom={"4px solid #F5F5F5"} />

          <Box display={"flex"} flex={1} flexDirection={"column"} gap={2}>
            <BorderedBox display={"flex"} flexDirection={"column"} gap={2}>
              {form.values?.content?.map((item, index) => (
                <Box
                  borderBottom={"4px solid #F5F5F5"}
                  pb={3}
                  display={"flex"}
                  flex={1}
                  flexDirection={"column"}
                  gap={2}
                  key={`${index}`}
                  position={"relative"}
                >
                  {index !== 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        right: 0,
                        top: 0,
                        display: "flex",
                        gap: 2,
                      }}
                    >
                      <Button
                        variant="text"
                        color="error"
                        onClick={() => makeDefaultLanguage(index)}
                      >
                        Mark it as default language
                      </Button>
                      <Button
                        variant="text"
                        color="error"
                        onClick={() => handleDelete(index)}
                      >
                        Delete
                      </Button>
                    </Box>
                  )}
                  <Typography
                    variant="h6"
                    color="GrayText"
                    sx={{ textTransform: "uppercase", letterSpacing: 3 }}
                  >
                    CONTENT:{" "}
                    {
                      options?.languages?.find(
                        (lang) => lang.value === item.lang_code
                      )?.label
                    }{" "}
                    {index === 0 && "(DEFAULT)"}
                  </Typography>
                  {element_type === "COMMUNICATION_TYPE_VIDEO" && (
                    <Box display={"flex"} gap={4}>
                      <Box
                        display={"flex"}
                        gap={2}
                        flexDirection={"column"}
                        flex={1}
                      >
                        {videoConfig
                          .map((item) => ({
                            ...item,
                            id: `content.${index}.${item.id}`,
                          }))
                          .map((item) => (
                            <Box>
                              <FormComponent
                                config={item}
                                form={form}
                                key={item.id}
                                otherOptions={
                                  options[item?.props?.optionsKey ?? item.id]
                                }
                              />
                              <ErrorLabel label={form.errors[item.id]} />
                            </Box>
                          ))}
                      </Box>
                      <Box
                        display={"flex"}
                        gap={2}
                        alignItems={"center"}
                        flex={1}
                      >
                        <Box display={"flex"} flexDirection={"column"} gap={2}>
                          <Label>Upload Video</Label>
                          <UploadVideoWrapper
                            UploadComponent={VideoUploader}
                            onUpload={handleVideoImage(index)}
                            url={form.getValue(`content.${index}.url`)}
                          />
                        </Box>
                        <Box display={"flex"} flexDirection={"column"} gap={2}>
                          <Label>Or add a video CDN URL</Label>
                          <TextField
                            sx={{
                              "& .MuiInputBase-input": {
                                padding: "8px !important",
                              },
                            }}
                            onChange={handleVideoUrl(
                              index,
                              `content.${index}.url`
                            )}
                            value={form.getValue(`content.${index}.url`)}
                            variant="outlined"
                            placeholder="CDN URL"
                          />
                          <ErrorLabel
                            label={form.errors[`content.${index}.url`]}
                          />

                          {form.getValue(`content.${index}.url`) &&
                            videoDurationConfig
                              .map((item) => ({
                                ...item,
                                id: `content.${index}.${item.id}`,
                                props: {
                                  ...item.props,
                                  disabled: !form.getValue(
                                    `content.${index}.url`
                                  ),
                                },
                              }))
                              .map((item) => (
                                <Box>
                                  <FormComponent
                                    config={item}
                                    form={form}
                                    key={item.id}
                                    otherOptions={
                                      options[
                                        item?.props?.optionsKey ?? item.id
                                      ]
                                    }
                                  />
                                  <ErrorLabel label={form.errors[item.id]} />
                                </Box>
                              ))}
                        </Box>
                      </Box>
                    </Box>
                  )}
                  {<ErrorLabel label={form.errors.content} />}
                  {element_type === "ASSESSMENT" && (
                    <QuizContainer>
                      <Box
                        display={"flex"}
                        gap={2}
                        flexDirection={"column"}
                        flex={1}
                        maxWidth={"520px"}
                        py={2}
                      >
                        {assessmentDetails
                          .map((item) => ({
                            ...item,
                            id: `content.${index}.${item.id}`,
                          }))
                          .map((item) => (
                            <Box>
                              <FormComponent
                                config={item}
                                form={form}
                                key={item.id}
                                otherOptions={
                                  options[item?.props?.optionsKey ?? item.id]
                                }
                              />
                              <ErrorLabel label={form.errors[item.id]} />
                            </Box>
                          ))}
                      </Box>
                      <QuizComponent
                        secIndex={index}
                        handleSectionChange={handleSelectionChange}
                        handleQuizQuestionAddition={handleQuestionAdd}
                        handleQuizQuestionDelete={handleQuestionDelete}
                        handleQuizOptionAddition={handleQuizOptionAddition}
                        handleOptionDelete={handleQuizOptionDelete}
                        disableQuestionEditing={() => false}
                        state={"create"}
                        items={item.questions}
                        type={form.values.assessment_type?.toLocaleLowerCase()}
                      />
                    </QuizContainer>
                  )}
                </Box>
              ))}
            </BorderedBox>
            <AddLanguageDialog
              options={options.languages}
              title={
                form?.values?.content?.length === 0
                  ? "Add a default language"
                  : "Add additional language"
              }
              onSelect={(language) => {
                const languagesAlreadyAdded = form?.values?.content?.map(
                  (item) => item.lang_code
                );

                if (languagesAlreadyAdded.includes(language)) {
                  dispatch(
                    setStatus({
                      message:
                        "this language is already added, please select a different language",
                      type: "error",
                    })
                  );
                } else {
                  handleAdd(language);
                  dispatch(
                    setStatus({
                      message: "language added",
                      type: "success",
                    })
                  );
                }
              }}
            />{" "}
            {form?.values?.content?.length === 0 && (
              <Box textAlign={"center"}>
                <ErrorLabel label={form.errors.content} />
              </Box>
            )}
          </Box>
        </>
      </RenderIf>
      <RenderIf condition={element_type === "SERIES"}>
        <Box>
          <Typography variant="h5">SERIES LIST</Typography>

          <Box>
            {form.values.sequence_details.map((item, index) => (
              <SeriesSelector
                title={`Section ${index + 1}`}
                defaultValue={item}
                key={index}
                onChange={onSeriesSectionChange(index)}
              />
            ))}
          </Box>
          <Button
            onClick={handleAddSection}
            endIcon={<Add />}
            color="error"
            sx={{ mt: 2 }}
          >
            Add section
          </Button>
        </Box>

        <Box>
          <Typography variant="h5">SERIES DETAILS</Typography>
          <Box maxWidth={"750px"} flex={1} display={"flex"} gap={2}>
            <Box display={"flex"} flexDirection={"column"} gap={2}>
              <Label>Portrait thumbnail</Label>
              <UploadImageWrapper
                UploadComponent={ImageUploader}
                onUpload={handleImage("portrait")}
                url={form.getValue("thumbnail.portrait")}
              />
              <ErrorLabel label={form.errors["thumbnail.portrait"]} />
            </Box>
            <Box display={"flex"} flexDirection={"column"} gap={2}>
              <Label>Landscape thumbnail</Label>
              <UploadImageWrapper
                UploadComponent={ImageUploader}
                onUpload={handleImage("landscape")}
                url={form.getValue("thumbnail.landscape")}
              />
              <ErrorLabel label={form.errors["thumbnail.landscape"]} />
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            The thumbnail aspect ratio is 0.8 for portrait and 1.66 for landscape.
          </Typography>
        </Box>
      </RenderIf>
    </Box>
  );
};

export default ElementForm;

export const ErrorLabel = ({ label }) => {
  if (!label) return null;
  return (
    <Typography
      variant="body2"
      sx={{ fontSize: "14px !important" }}
      color="error"
    >
      {label}
    </Typography>
  );
};

const BorderedBox = styled(Box)`
  background: #fcfcfc;
  border: 1px solid #cfcfcf;
  box-sizing: border-box;
  border-radius: 12px;
  padding: 30px;
`;
